/* eslint-disable max-len */
import * as Leaflet from "leaflet";
import { FormStyle, TranslationWidth, getLocaleMonthNames, registerLocaleData } from "@angular/common";
import { FontSpec } from "chart.js";
import localeEN from "@angular/common/locales/en";
import localeES from "@angular/common/locales/es";
import localeCA from "@angular/common/locales/ca";
import { AllFloodsLayer, BaseMapLayer, EquipamentsLayer, MapLayerWMS, PlantesBaixesLayer, SoterranisLayer } from "../types";
import {
  AppRoutes,
  BaseMapLayers,
  EquipamentsLayers,
  DataModes,
  DataModeTime,
  DataModeType,
  DataModeVariable,
  MapLayersType,
  MapLayersWMS,
  PlantesBaixesLayers,
  SoterranisLayers,
  AllFloodsLayers,
} from "../enums";
import { WindSpeedAlertLevels } from "../interfaces";

registerLocaleData(localeEN);
registerLocaleData(localeES);
registerLocaleData(localeCA);

export const APP_NAME = "RiscBal App";

export const WIND_SPEED = "WindSpeed";

export const WIND_SPEED_ALERT_LEVELS: WindSpeedAlertLevels = {
  level1WindSpeed: 70,
  level2WindSpeed: 90,
  level3WindSpeed: 120,
};

export const WIND_SPEED_LEVELS = {
  WIND_CALM: 6,
  WIND_WEAK: 20,
  WIND_MODERATED: 40,
  WIND_STRONG: 70,
};

export const BASE_MAP_LAYERS: BaseMapLayer = {
  OpenStreetMaps: {
    name: BaseMapLayers.OpenStreetMaps,
    wmsConfig: {
      baseUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      options: {
        maxZoom: 19,
        minZoom: 6,
        attribution: "&copy; <a href='http://www.openstreetmap.org/copyright'>OpenStreetMap</a>",
      },
    },
  },
  BiN: {
    name: BaseMapLayers.BiN,
    wmsConfig: {
      baseUrl: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_25j6_1_9f3c77591aa910c2283379a6",
      options: {
        maxZoom: 19,
        minZoom: 6,
        attribution: "USGS, NOAA",
      },
    },
  },
  Ideib: {
    name: BaseMapLayers.Ideib,
    wmsConfig: {
      baseUrl: "https://ideib.caib.es/geoserveis/services/public/GOIB_MapaBase_IB/MapServer/WMSServer?",
      options: {
        maxZoom: 19,
        layers: "0",
      },
    },
  },
  Ortofoto: {
    name: BaseMapLayers.Ortofoto,
    wmsConfig: {
      baseUrl: "https://ideib.caib.es/geoserveis/services/imatges/GOIB_Ortofoto_2021_IB/MapServer/WMSServer?",
      options: {
        maxZoom: 19,
        layers: "0",
      },
    },
  },
};

export const DATA_MODE_INITIAL_STATE = {
  name: DataModes.WaterLevel,
  variable: DataModeVariable.WaterLevel,
  time: DataModeTime.Time1h,
  dataType: DataModeType.Observed,
};

export const MAP_LAYERS_WMS: MapLayerWMS = {
  None: {
    name: MapLayersWMS.None,
    time: null,
    type: MapLayersType.None,
    baseUrl: "",
    options: null,
  },
  Water1h: {
    type: MapLayersType.Water,
    name: MapLayersWMS.Water1h,
    time: "1h",
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms?",
    options: {
      maxZoom: 19,
      layers: "visor_temporal_radar:runoff_1H",
      transparent: true,
      format: "image/png",
      styles: "BB_1h",
    },
  },
  Water24h: {
    type: MapLayersType.Water,
    name: MapLayersWMS.Water24h,
    time: "24h",
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms?",
    options: {
      maxZoom: 19,
      layers: "visor_temporal_radar:runoff_24H",
      transparent: true,
      format: "image/png",
      styles: "BB_24h",
    },
  },
  Precipitation1h: {
    type: MapLayersType.Precipitation,
    name: MapLayersWMS.Precipitation1h,
    time: "1h",
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms?",
    options: {
      maxZoom: 19,
      layers: "visor_temporal_radar:accrain_1H",
      transparent: true,
      format: "image/png",
      styles: "AEMET_1h_intervals",
    },
  },
  Precipitation24h: {
    type: MapLayersType.Precipitation,
    name: MapLayersWMS.Precipitation24h,
    time: "24h",
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms?",
    options: {
      maxZoom: 19,
      layers: "visor_temporal_radar:accrain_24H",
      transparent: true,
      format: "image/png",
      styles: "AEMET_24h_intervals",
    },
  },
  RiversZoomIn: {
    type: MapLayersType.Rivers,
    name: MapLayersWMS.RiversZoomIn,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "visor_emergencies:torrents_detall",
      transparent: true,
      format: "image/png",
    },
  },
  RiversZoomOut: {
    type: MapLayersType.Rivers,
    name: MapLayersWMS.RiversZoomOut,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms?",
    options: {
      maxZoom: 19,
      layers: "visor_emergencies:rivers_regional",
      transparent: true,
      format: "image/png",
    },
  },
};

export const ALL_FLOODS_LAYER: AllFloodsLayer = {
  None: {
    name: AllFloodsLayers.None,
    type: MapLayersType.None,
    baseUrl: "",
    options: null,
  },
  Calat_T010: {
    returnPeriod: "T010",
    name: AllFloodsLayers.Calat_T010,
    type: MapLayersType.Calat_T010,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:calat_simple_T010",
      transparent: true,
      format: "image/png",
    },
  },
  Calat_T100: {
    returnPeriod: "T100",
    name: AllFloodsLayers.Calat_T100,
    type: MapLayersType.Calat_T100,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:calat_simple_T100",
      transparent: true,
      format: "image/png",
    },
  },
  Calat_T500: {
    returnPeriod: "T500",
    name: AllFloodsLayers.Calat_T500,
    type: MapLayersType.Calat_T500,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:calat_simple_T500",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentPeatons_T010: {
    returnPeriod: "T010",
    name: AllFloodsLayers.IndexArrossegamentPeatons_T010,
    type: MapLayersType.IndexArrossegamentPeatons_T010,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iap_T010",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentPeatons_T100: {
    returnPeriod: "T100",
    name: AllFloodsLayers.IndexArrossegamentPeatons_T100,
    type: MapLayersType.IndexArrossegamentPeatons_T100,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iap_T100",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentPeatons_T500: {
    returnPeriod: "T500",
    name: AllFloodsLayers.IndexArrossegamentPeatons_T500,
    type: MapLayersType.IndexArrossegamentPeatons_T500,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iap_T500",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentVehicles_T010: {
    returnPeriod: "T010",
    name: AllFloodsLayers.IndexArrossegamentVehicles_T010,
    type: MapLayersType.IndexArrossegamentVehicles_T010,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iav_T010",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentVehicles_T100: {
    returnPeriod: "T100",
    name: AllFloodsLayers.IndexArrossegamentVehicles_T100,
    type: MapLayersType.IndexArrossegamentVehicles_T100,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iav_T100",
      transparent: true,
      format: "image/png",
    },
  },
  IndexArrossegamentVehicles_T500: {
    returnPeriod: "T500",
    name: AllFloodsLayers.IndexArrossegamentVehicles_T500,
    type: MapLayersType.IndexArrossegamentVehicles_T500,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:iav_T500",
      transparent: true,
      format: "image/png",
    },
  },
  Velocitat_T010: {
    returnPeriod: "T010",
    name: AllFloodsLayers.Velocitat_T010,
    type: MapLayersType.Velocitat_T010,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:velocitat_simple_T010",
      transparent: true,
      format: "image/png",
    },
  },
  Velocitat_T100: {
    returnPeriod: "T100",
    name: AllFloodsLayers.Velocitat_T100,
    type: MapLayersType.Velocitat_T100,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:velocitat_simple_T100",
      transparent: true,
      format: "image/png",
    },
  },
  Velocitat_T500: {
    returnPeriod: "T500",
    name: AllFloodsLayers.Velocitat_T500,
    type: MapLayersType.Velocitat_T500,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:velocitat_simple_T500",
      transparent: true,
      format: "image/png",
    },
  },
  AreaInundable_T010: {
    returnPeriod: "T010",
    name: AllFloodsLayers.AreaInundable_T010,
    type: MapLayersType.AreaInundable_T010,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:area_inundable_T010",
      transparent: true,
      format: "image/png",
    },
  },
  AreaInundable_T100: {
    returnPeriod: "T100",
    name: AllFloodsLayers.AreaInundable_T100,
    type: MapLayersType.AreaInundable_T100,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:area_inundable_T100",
      transparent: true,
      format: "image/png",
    },
  },
  AreaInundable_T500: {
    returnPeriod: "T500",
    name: AllFloodsLayers.AreaInundable_T500,
    type: MapLayersType.AreaInundable_T500,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:area_inundable_T500",
      transparent: true,
      format: "image/png",
    },
  },
  ZonaFluxPreferent: {
    name: AllFloodsLayers.ZonaFluxPreferent,
    type: MapLayersType.ZonaFluxPreferent,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "inundacions:zfp_T100",
      transparent: true,
      format: "image/png",
    },
  },
};

export const EQUIPAMENTS_LAYER: EquipamentsLayer = {
  None: {
    name: EquipamentsLayers.None,
    type: MapLayersType.None,
    baseUrl: "",
    options: null,
  },
  Equipaments: {
    name: EquipamentsLayers.Equipaments,
    type: MapLayersType.Equipaments,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "visor_emergencies:T500_EQUIPAMENTS",
      transparent: true,
      format: "image/png",
    },
  },
};

export const PLANTES_BAIXES_LAYER: PlantesBaixesLayer = {
  None: {
    name: PlantesBaixesLayers.None,
    type: MapLayersType.None,
    baseUrl: "",
    options: null,
  },
  PlantaBaixa: {
    name: PlantesBaixesLayers.PlantaBaixa,
    type: MapLayersType.PlantaBaixa,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "visor_emergencies:T500_PLANTES_BAIXES",
      transparent: true,
      format: "image/png",
    },
  },
};

export const SOTERRANIS_LAYER: SoterranisLayer = {
  None: {
    name: SoterranisLayers.None,
    type: MapLayersType.None,
    baseUrl: "",
    options: null,
  },
  Soterrani: {
    name: SoterranisLayers.Soterrani,
    type: MapLayersType.Soterrani,
    baseUrl: "https://geoserver.riscbal.uib.cat/geoserver/wms",
    options: {
      maxZoom: 19,
      layers: "visor_emergencies:T500_SOTERRANIS",
      transparent: true,
      format: "image/png",
    },
  },
};

export const PLUJA_RADAR_URL = "https://geoserver.riscbal.uib.cat/pluja_radar";

export const MOBILE_MAX_WIDTH = 768;

export const DEFAULT_LANGUAGE = "ca";

export const AVAILABLE_LANGUAGES = ["en", "es", "ca"];

export const INTRODUCTION_ROUTES: AppRoutes[] = [AppRoutes.welcome, AppRoutes.login];

export const NO_INTERCEPTOR_ROUTES: AppRoutes[] = [AppRoutes.login];

export const NO_INTERCEPTOR_REQUESTS: string[] = ["refresh"];

export const MAP_OPTIONS: Leaflet.MapOptions = {
  maxZoom: 19,
  zoom: 9,
  center: { lat: 39.6, lng: 2.9 },
};

export const CLUSTER_OPTIONS: Leaflet.MarkerClusterGroupOptions = {
  spiderfyOnMaxZoom: false,
  showCoverageOnHover: false,
  removeOutsideVisibleBounds: true,
  disableClusteringAtZoom: 14,
  iconCreateFunction: function (cluster): Leaflet.DivIcon {
    let maxIcon: Leaflet.DivIcon;
    const markerArray = cluster.getAllChildMarkers();
    for (let m in markerArray) {
      if (m) {
        const marker = markerArray[m];
        const icon = marker.getIcon();
        if (!maxIcon || Number(icon.options.className) > Number(maxIcon.options.className)) maxIcon = icon;
      }
    }
    return maxIcon;
  },
};

export const monthNames: { [key in string]: readonly string[] } = {
  en: getLocaleMonthNames("en", FormStyle.Format, TranslationWidth.Wide),
  es: getLocaleMonthNames("es", FormStyle.Format, TranslationWidth.Wide),
  ca: getLocaleMonthNames("ca", FormStyle.Format, TranslationWidth.Wide),
};

export const REFRESH_TIME_SECONDS = 60;

export const GROUPPED_CATCHMENTS = [
  113, 126, 114, 115, 109, 110, 121, 129, 103, 107, 76, 104, 122, 117, 96, 111, 128, 84, 105, 108, 125, 64, 116, 124, 118, 123, 127, 112,
  119, 130, 299, 300,
];

// Colors for chart alert zones
export const CHART_LEVEL4_ZONE_COLOR = "rgba(197, 20, 62, 0.4)";
export const CHART_LEVEL3_ZONE_COLOR = "rgba(230, 126, 20, 0.4)";
export const CHART_LEVEL2_ZONE_COLOR = "rgba(230, 183, 16, 0.4)";
export const CHART_LEVEL1_ZONE_COLOR = "rgba(20, 197, 148, 0.3)";

// Chart colors
export const PRIMARY_LINE_COLOR: string = "#1d4780";
export const SECONDARY_LINE_COLOR: string = "#4d89db";
export const LABELS_COLOR: string = "#868782";
export const GRID_LINES_COLOR: string = "#d9d9d9";

export const FONT: Partial<FontSpec> = { family: "AppFont", weight: "normal", size: 12 };
export const CHART_CURRENT_TIME_WIDTH = 5;

export const SHARE_BUTTON_BASE_URL = "https://visor.riscbal.uib.cat";
